/**
  * GOOGLE ANALYTICS EVENT HOW TO:
  * Basic structure of a Google Analytics event object looks like this:
  *
  * EVENT_NAME_CONSTANT = {
  *     category: String (required),
  *     action: (required),
  *     label: (optional),
  *     value: (optional)
  * }
  *
  * Some GA events take dynamic data for some of the above properties. For example, a REPORT_LISTING event might
  * have a label based on the current page name. If so, we set the property in the constant file with a value of
  * WILL_GET_CUSTOM_DATA. This shows that we don't know what it will be, but we expect to the gaEvent to have
  * it when send the request to Google analytics.
  */

import {
    customDimensions,
} from '@zg-rentals/analytics-js';

// Indicates a particular property will receive dynamic data as additionalParams when track.GAEvent method is called.
// TODO: Rename? "CUSTOM" isn't exactly correct. It's just additionalParams that are dynamic based on what's
// passed into our analyticsUtils.
const WILL_GET_CUSTOM_DATA = true;

const CLAIM_LISTING_CATEGORY = 'FeedClaimAndConvert';
const CLAIM_LISTINGS_LABEL = {
    FIND_LISTING_RESULTS: 'FindListingResults',
    PROPERTY_LOOKUP_MODAL: 'PropertyLookupModal',
    PHONE_LOOKUP_MODAL: 'PhoneLookupModal',
    EMAIL_LOOKUP_MODAL: 'EmailLookupModal',
    LISTING_RESULTS_MODAL: 'ListingResultsModal',
    LISTING_LOOKUP_SUCCESS: 'ListingLookupSuccess',
    CONTACT_VERIFICATION: 'ContactVerification',
    PHONE_VERIFICATION_MODAL: 'PhoneVerificationModal',
    CONTACT_VERIFICATION_SUCCESSFUL: 'ContactVerificationSuccessful',
    TRANSFERT_INTRO: 'TransferListingsIntro',
    TRANSFERT_PROCESSING: 'TransferListingsProcessing',
    TRANSFERT_COMPLETE: 'TransferListingsComplete',
};

/**
 *  A group of field names used for GA errors
 */
const LISTING_COMPONENT_GROUP_NAME = {
    ADDRESS: 'Address',
    AMENITIES: 'Amenities',
    CONTACT_INFO: 'ContactInfo',
    DETAILS_DESCRIPTION: 'DetailsDescription',
    BUILDING_DETAILS: 'BuildingDetails',
};

const SUFFIX = {
    LEAD_GUARANTEE: '-LeadGuarantee',
};

const TOUR_TYPE = {
    floorplan: 'FloorPlan',
    unit: 'Unit',
    amenity: 'Amenity',
    singleFamily: 'SingleFamily',
};

const TOUR_LOCATION = {
    MODAL: '3DTourModal',
    CARD: '3DTourCard',
};

const GA_EVENTS = {
    FOOTER_NAV_CLICK_TO: {
        category: 'FooterNav',
        action: WILL_GET_CUSTOM_DATA,
        description: 'Track when a user clicks a link in the Footer Nav.',
    },
    ADD_PROPERTY_MODAL: {
        category: 'PostingPath',
        label: 'ListingManagement',
        action: 'ViewAddPropertyModal',
        description: 'Track when a user interacts with the Add Property modal.',
    },
    ADD_PROPERTY_MODAL_DISMISS: {
        category: 'PostingPath',
        label: 'ListingManagement',
        action: 'DismissAddPropertyModal',
    },
    ADD_PROPERTY_MODAL_CTA: {
        category: 'PostingPath',
        label: 'ListingManagement',
        action: WILL_GET_CUSTOM_DATA,
    },
    ARCHIVE_LISTING: {
        category: 'PostingPath',
        label: 'ListingManagement',
        action: 'ArchiveProperty',
        description: 'Track when a user archives property.',
    },
    UNARCHIVE_LISTING: {
        category: 'PostingPath',
        label: 'ListingManagement',
        action: 'UnarchiveProperty',
        description: 'Track when a user unarchives property.',
    },
    ADD_UNIT: {
        category: 'PostingPath',
        label: 'ListingManagement',
        action: 'AddUnit',
        description: 'Track when a user add a unit',
    },
    TOGGLE_AVAILABILITY_UNIT: {
        category: 'PostingPath',
        label: 'ListingManagement',
        action: WILL_GET_CUSTOM_DATA,
        description: 'Track when a user enable/disable a unit',
    },
    ADD_FLOORPLAN: {
        category: 'PostingPath',
        label: 'ListingManagement',
        action: 'AddFloorplan',
        description: 'Track when a user add a floorplan',
    },
    TOGGLE_AVAILABILITY_FLOORPLAN: {
        category: 'PostingPath',
        label: 'ListingManagement',
        action: WILL_GET_CUSTOM_DATA,
        description: 'Track when a user enable/disable a floorplan',
    },
    LEARN_MORE_TOUR_CLICK: {
        category: 'PostingPath',
        label: TOUR_LOCATION.CARD,
        action: 'LearnMoreTour',
        description: 'Track when a user clicks on 3d tour learn more',
    },
    LOAD_TOURS: {
        category: 'PostingPath',
        label: WILL_GET_CUSTOM_DATA,
        action: 'LoadTours',
        description: 'Track when a user loaded tours',
    },
    ADD_TOUR: {
        category: 'PostingPath',
        label: WILL_GET_CUSTOM_DATA,
        action: 'AddTour',
        description: 'Track when a user adds a tour',
    },
    PUBLISH_TOUR: {
        category: 'PostingPath',
        label: WILL_GET_CUSTOM_DATA,
        action: 'PublishTour',
        description: 'Track when a user publishes a tour',
    },
    UNPUBLISH_TOUR: {
        category: 'PostingPath',
        label: WILL_GET_CUSTOM_DATA,
        action: 'UnpublishTour',
        description: 'Track when a user unpublishes a tour',
    },
    VIEW_TOUR: {
        category: 'PostingPath',
        label: WILL_GET_CUSTOM_DATA,
        action: 'ViewTour',
        description: 'Track when a user views a tour',
    },
    EDIT_TOUR: {
        category: 'PostingPath',
        label: WILL_GET_CUSTOM_DATA,
        action: 'EditTour',
        description: 'Track when a user edits a tour',
    },
    REMOVE_TOUR: {
        category: 'PostingPath',
        label: WILL_GET_CUSTOM_DATA,
        action: 'RemoveTour',
        description: 'Track when a user removes a tour',
    },
    SELECT_TOUR: {
        category: 'PostingPath',
        label: WILL_GET_CUSTOM_DATA,
        action: 'SelectTour',
        description: 'Track when a user selects a tour',
    },
    UNSELECT_TOUR: {
        category: 'PostingPath',
        label: WILL_GET_CUSTOM_DATA,
        action: 'UnselectTour',
        description: 'Track when a user unselects a tour',
    },
    SUBMIT_TOUR: {
        category: 'PostingPath',
        label: WILL_GET_CUSTOM_DATA,
        action: 'SubmitTour',
        description: 'Track when a user submits a tour',
    },

    /**
     * @param {string} action - A placeholder for GA action
     *
     * The placeholder will be replaced by the listing component group name + Error (string)
     * ex: AddressError
     *
     * The group names are defined in LISTING_COMPONENT_GROUP_NAME constants
     */
    LISTING_INPUT_ERROR: {
        category: 'PostingPath',
        label: 'InputError',
        action: 'ListingInputError',
        description: 'Track when a user had a listing input error',
    },
    FLOORPLAN_LAYOUT_PHOTO_UPLOAD_SUCCESS: {
        category: 'PostingPath',
        label: 'Photos',
        action: 'LayoutPhotoUpload',
        description: 'Track when a layout photo for a floor plan is successfully uploaded.',
    },
    FLOORPLAN_LAYOUT_PHOTO_UPLOAD_ERROR: {
        category: 'PostingPath',
        label: 'Photos',
        action: 'LayoutPhotoUpload',
        description: 'Track when an error occurs after a user tries to upload a layout photo to a floor plan.',
    },
    FLOORPLAN_PHOTO_UPLOAD_SUCCESS: {
        category: 'PostingPath',
        label: 'Photos',
        action: 'FloorplanPhotoUpload',
        description: 'Track when a photo for a floor plan is successfully uploaded.',
    },
    FLOORPLAN_PHOTO_UPLOAD_ERROR: {
        category: 'PostingPath',
        label: 'Photos',
        action: 'FloorplanPhotoUploadError',
        description: 'Track when an error occurs after a user tries to upload a photo to a floor plan.',
    },
    HIDE_PHONE: {
        category: 'PostingPath',
        label: 'ListingManagement',
        action: 'HidePhone',
        description: 'Track when a user hides the contact phone number',
    },
    SHOW_PHONE: {
        category: 'PostingPath',
        label: 'ListingManagement',
        action: 'ShowPhone',
        description: 'Track when a user shows the contact phone number',
    },
    EXTEND_LISTING_NON_NUCLEUS_MARKET: {
        category: 'PostingPath',
        label: 'ListingManagement',
        action: 'ExtendListing-Non-Nucleus',
        description: 'Track when a user extends their listing outside of Nucleus market.',
    },

    EXTEND_LISTING_FREE_NUCLEUS_MARKET: {
        category: 'PostingPath',
        label: 'ListingManagement',
        action: 'ExtendListing-Nucleus-Free',
        description: 'Track when a user extends their listing outside of Nucleus market.',
    },

    LOGO_UPLOAD_SUCCESS: {
        category: 'PostingPath',
        label: 'Photos',
        action: 'LogoUpload',
        description: 'Track when a logo is successfully uploaded.',
    },
    LOGO_UPLOAD_ERROR: {
        category: 'PostingPath',
        label: 'Photos',
        action: 'LogoUploadError',
        description: 'Track if an error occurs while a user tries to upload a logo.',
    },
    EXISTING_LISTING_REDIRECT: {
        category: 'PostingPath',
        label: 'ListingManagement',
        action: 'ExistingListingRedirect',
        description: 'Track when a user is redirected to an existing listing upon creation attempt.',
    },
    EXISTING_LISTING_POSTED_WARNING: {
        category: 'PostingPath',
        label: 'ListingManagement',
        action: 'ListingAlreadyPostedWarning',
    },
    STATUS_LINK_CLICK: {
        category: 'PostingPath',
        label: 'ListingManagement',
        action: WILL_GET_CUSTOM_DATA,
    },
    HPPP_NEW_LISTING_ERROR: {
        category: 'PostingPath',
        label: 'CreateNewListingError',
        action: WILL_GET_CUSTOM_DATA,
        description: 'Track if an error occurs while trying to create a brand new (empty) listing on the HPPP.',
    },
    PHOTO_UPLOAD_SUCCESS: {
        category: 'PostingPath',
        label: 'Photos',
        action: 'ListingPhotoUpload',
        description: 'Track when a photo is successfully uploaded.',
    },
    PHOTO_UPLOAD_ERROR: {
        category: 'PostingPath',
        label: 'Photos',
        action: 'ListingPhotoUploadError',
        description: 'Track when an error occurs when a user tries to upload a photo to a listing on the HPPP.',
    },
    HPPP_VERIFICATION_ERROR: {
        category: 'PostingPath',
        label: 'ContactInfo',
        action: 'verificationNotSuccessful',
        description: 'Track when an error occurs after user tries to verify their phone number on the HPPP.',
    },
    HPPP_VERIFICATION_REQUEST: {
        category: 'PostingPath',
        label: 'ContactInfo',
        action: 'requestVerification',
        description: 'Track when a user requests a verification code from our API on the HPPP.',
    },
    HPPP_VERIFICATION_SUCCESS: {
        category: 'PostingPath',
        label: 'ContactInfo',
        action: 'verificationSuccessful',
        description: 'Track when a user is able to successfully verify their phone number on the HPPP.',
    },
    NAV_BAR_CLICK: {
        category: 'NavBarClick',
        action: WILL_GET_CUSTOM_DATA,
        label: WILL_GET_CUSTOM_DATA,
        description: 'Track any links a user clicks on the NavBar.',
    },
    APPLICATIONS_INVITE_TO_APPLY: {
        category: 'Applications',
        label: 'IndividualLeadPage_InviteToApply',
        action: 'click',
    },
    APPLICATIONS_MODAL_ENABLE: {
        category: 'Applications',
        label: 'MarketingPathModal',
        action: 'Enable',
    },
    APPLICATIONS_MODAL_DISMISS: {
        category: 'Applications',
        label: 'MarketingPathModal',
        action: 'Dismiss',
    },
    APPLICATIONS_RADIO_CHANGE: {
        category: 'Applications',
        label: 'EditListingPage',
        action: WILL_GET_CUSTOM_DATA,
    },
    APPLICATIONS_POST_ACTIVATION_MODAL_ENABLE: {
        category: 'Applications',
        label: 'PostActivationModal',
        action: 'Enable',
    },
    APPLICATIONS_POST_ACTIVATION_MODAL_DISMISS: {
        category: 'Applications',
        label: 'PostActivationModal',
        action: 'Dismiss',
    },
    POST_DEACTIVATION_MODAL_DISPLAY: {
        category: 'Payments',
        label: 'PostDeactivationModal',
        action: 'Displayed',
    },
    POST_DEACTIVATION_MODAL_DISMISS: {
        category: 'Payments',
        label: 'PostDeactivationModal',
        action: 'Dismiss',
    },
    POST_DEACTIVATION_MODAL_COLLECT_RENT_CLICK: {
        category: 'Payments',
        label: 'PostDeactivationModal',
        action: 'OnboardingClick',
    },
    POST_DEACTIVATION_MODAL_DONT_NEED_CLICK: {
        category: 'Payments',
        label: 'PostDeactivationModal',
        action: 'DontNeedClick',
    },
    PAYMENTS_GET_STARTED_CLICK: {
        category: 'Payments',
        label: 'IreneOnboarding',
        action: 'StartCollecting',
    },
    DOWNLOAD_DATA_CLICK: {
        category: WILL_GET_CUSTOM_DATA,
        action: 'ClickDownload',
        label: 'Analytics',
        description: 'Track when a user downloads data',
    },
    REVIEW_COLUMN_CLICK: {
        category: 'Funnel Page',
        action: 'ClickReviews',
        label: 'redirect',
        description: 'Track when a user clicks on the review column',
    },
    PROPERTY_COLUMN_CLICK: {
        category: WILL_GET_CUSTOM_DATA,
        action: 'ClickProperty',
        label: 'redirect',
        description: 'Track when a user clicks on the property column',
    },
    INQUIRY_COLUMN_CLICK: {
        category: WILL_GET_CUSTOM_DATA,
        action: 'ClickInquiry',
        label: 'redirect',
        description: 'Track when a user clicks on the inquiry column',
    },
    AD_SPEND_SWITCHER_CLICK: {
        category: WILL_GET_CUSTOM_DATA,
        action: 'ClickSwitcher',
        label: 'redirect',
        description: 'Track when a user switch between all spend / boost page',
    },
    VIEW_BOOST_AD: {
        category: 'Boost Spend Page',
        action: 'ViewAdClick',
        description: 'Track when a user clicks on the view ad column',
    },
    LOAD_BOOST_AD: {
        category: 'Boost Spend Page',
        action: 'LoadAd',
        description: 'Track when a user loads the boost ad from an url click',
    },
    CREATE_REVIEW_RESPONSE: {
        category: 'Reviews',
        action: 'CreateReviewResponse',
        description: 'Track when a user responds to a review',
    },
    DELETE_REVIEW_RESPONSE: {
        category: 'Reviews',
        action: 'DeleteReviewResponse',
        description: 'Track when a user deletes a response to a review',
    },
    REPORT_REVIEW: {
        category: 'Reviews',
        label: WILL_GET_CUSTOM_DATA,
        action: 'ReportReview',
        description: 'Track when a user reports a review',
    },
    SEARCH_REVIEWS: {
        category: 'Reviews',
        action: 'SearchReviews',
        description: 'Track when a user searches for reviews',
    },
    HIDE_REVIEWS: {
        category: 'Reviews',
        label: WILL_GET_CUSTOM_DATA,
        action: 'HideReviewsWithResponses',
        description: 'Track when a user hides/shows reviews with responses',
    },
    SORT_REVIEWS: {
        category: 'Reviews',
        label: WILL_GET_CUSTOM_DATA,
        action: 'SortReviews',
        description: 'Track when a user sorts reviews',
    },
    NOTIFICATION_REVIEWS: {
        category: 'Reviews',
        label: WILL_GET_CUSTOM_DATA,
        action: 'notificationReviews',
        description: 'Track when a user wants to start or stop receiving review notifications',
    },
    HPPP_MIGRATION_BLOCKER_CTA: {
        category: 'HpppMigration',
        label: 'Blocker',
        action: 'GoToZrm',
        description: 'Track when a user clicks on the HPPP CTA in the blocker to migrate over to ZRM',
    },
    // GA Event for Payments/Applications click events
    PROPERTIES_PAYMENTS_ROUTE_CLICK: {
        category: 'Payments',
        label: 'IreneNavigation',
        action: 'ClickFromMyProperties',
    },
    PROPERTIES_APPLICATIONS_ROUTE_CLICK: {
        category: 'Applications',
        label: 'IreneNavigation',
        action: 'ClickFromMyProperties',
    },
    // rental manager landing page GA events
    MAIN_GET_STARTED: {
        category: 'ZRM Start Page Entry',
        label: 'Main - Get Started',
        action: 'Top Button',
    },
    MAIN_VIEW_YOUR_PROPERTIES: {
        category: 'ZRM Start Page Entry',
        label: 'Main - View Your Properties',
        action: 'Top Button',
    },
    // rental manager post-a-listing page GA events
    POST_LISTING_GET_STARTED: {
        category: 'ZRM Start Page Entry',
        label: 'Post Listing - Get Started',
        action: 'Top Button',
    },
    // rental manager leasing GA events
    PROPERTIES_LEASES_ROUTE_CLICK: {
        category: 'Leases',
        label: 'IreneNavigation',
        action: 'ClickFromMyProps',
    },

    // maintenance
    MAINTENANCE_BANNER_DISPLAYED_KEEPE: {
        category: 'ZRM Maintenance',
        label: 'Keepe',
        action: 'Banner Displayed',
    },
    MAINTENANCE_BANNER_DISPLAYED_HOMEE: {
        category: 'ZRM Maintenance',
        label: 'Homee',
        action: 'Banner Displayed',
    },
    MAINTENANCE_BANNER_CLICK_KEEPE: {
        category: 'ZRM Maintenance',
        label: 'Keepe',
        action: 'Referral Clicked',
    },
    MAINTENANCE_BANNER_CLICK_HOMEE: {
        category: 'ZRM Maintenance',
        label: 'Homee',
        action: 'Referral Clicked',
    },

    // notifications
    // notifications - pre prompt
    NOTIFICATION_PRE_PROMPT_DISPLAYED: {
        category: 'Browser Notifications',
        label: WILL_GET_CUSTOM_DATA,
        action: 'Displayed',
    },
    NOTIFICATION_PRE_PROMPT_GRANTED: {
        category: 'Browser Notifications',
        label: WILL_GET_CUSTOM_DATA,
        action: 'Permission Granted',
    },
    NOTIFICATION_PRE_PROMPT_DENIED: {
        category: 'Browser Notifications',
        label: WILL_GET_CUSTOM_DATA,
        action: 'Permission Denied',
    },
    NOTIFICATION_PRE_PROMPT_DISMISSED: {
        category: 'Browser Notifications',
        label: WILL_GET_CUSTOM_DATA,
        action: 'Permission Dismissed',
    },

    // notifications - browser prompt
    NOTIFICATION_PROMPT_DISPLAYED: {
        category: 'Browser Notifications',
        label: WILL_GET_CUSTOM_DATA,
        action: 'Displayed',
    },
    NOTIFICATION_PROMPT_GRANTED: {
        category: 'Browser Notifications',
        label: WILL_GET_CUSTOM_DATA,
        action: 'Permission Granted',
    },
    NOTIFICATION_PROMPT_DENIED: {
        category: 'Browser Notifications',
        label: WILL_GET_CUSTOM_DATA,
        action: 'Permission Denied',
    },
    // notifications - event
    NOTIFICATION_DISPLAYED: {
        category: 'Browser Notifications',
        label: WILL_GET_CUSTOM_DATA,
        action: 'Displayed',
    },
    NOTIFICATION_DISMISSED: {
        category: 'Browser Notifications',
        label: WILL_GET_CUSTOM_DATA,
        action: 'Dismissed',
    },
    NOTIFICATION_CLICKED: {
        category: 'Browser Notifications',
        label: WILL_GET_CUSTOM_DATA,
        action: 'Clicked',
    },
    // Paid inclusion events
    VALUE_PROP_VIEW: {
        category: 'PostingPath',
        label: 'ListingManagement',
        action: 'ViewValueProp',
    },
    VALUE_PROP_DISMISS: {
        category: 'PostingPath',
        label: 'ListingManagement',
        action: 'DismissValueProp',
    },
    SECONDARY_CHECKOUT_DEFAULT_PAYMENT_METHOD: {
        category: 'PostingPath',
        label: 'ListingManagement',
        action: 'UseExistingPaymentMethod',
    },
    SECONDARY_CHECKOUT_NEW_PAYMENT_METHOD: {
        category: 'PostingPath',
        label: 'ListingManagement',
        action: 'ProvideNewPaymentMethod',
    },
    DEACTIVATE_BEGIN: {
        category: 'PostingPath',
        label: 'ListingManagement',
        action: 'BeginDeactivation',
        description: 'User enters the deactivation flow',
    },
    DEACTIVATE_NOW: {
        category: 'PostingPath',
        label: 'ListingManagement',
        action: 'DeactivateListing-Now',
        description: 'User deactivates a paid listing now',
    },
    DEACTIVATE_SCHEDULE: {
        category: 'PostingPath',
        label: 'ListingManagement',
        action: 'DeactivateListing-Schedule',
        description: 'User deactivates a paid listing at the end of current term',
    },
    DEACTIVATE_DISMISSED: {
        category: 'PostingPath',
        label: 'ListingManagement',
        action: 'DeactivationScreenDismissed',
    },
    CHECKOUT_BEGIN: {
        category: 'PostingPath',
        label: 'ListingManagement',
        action: 'BeginCheckout',
        description: 'User enters the checkout flow',
    },
    CHECKOUT_DISMISSED: {
        category: 'PostingPath',
        label: 'ListingManagement',
        action: 'CheckoutFormDismissed',
    },
    CHECKOUT_FAILURE: {
        category: 'PostingPath',
        label: 'ListingManagement',
        action: 'PaidActivationFailed',
    },
    REACTIVATE_WITHIN_WINDOW: {
        category: 'PostingPath',
        label: 'ListingManagement',
        action: 'ReactivateWithinWindow',
    },
    ACTIVATE_LISTING_NON_NUCLEUS: {
        category: 'PostingPath',
        label: 'ListingManagement',
        action: 'ActivateListing-Non-Nucleus',
    },
    ACTIVATE_LISTING_NUCLEUS_FREE: {
        category: 'PostingPath',
        label: 'ListingManagement',
        action: 'ActivateListing-Nucleus-Free',
    },
    ACTIVATE_LISTING_NUCLEUS_PAID: {
        category: 'PostingPath',
        label: 'ListingManagement',
        action: 'ActivateListing-Nucleus-Paid',
        description: 'User activates a paid Nucleus listing',
    },
    LISTING_PAGE_VIEW_OFFMARKET_NON_NUCLEUS: {
        category: 'PostingPath',
        label: 'ListingManagement',
        action: 'ListingPageview-offmarket-Non-Nucleus',
    },
    LISTING_PAGE_VIEW_OFFMARKET_NUCLEUS_FREE: {
        category: 'PostingPath',
        label: 'ListingManagement',
        action: 'ListingPageview-offmarket-Nucleus-Free',
    },
    LISTING_PAGE_VIEW_OFFMARKET_NUCLEUS_PAID: {
        category: 'PostingPath',
        label: 'ListingManagement',
        action: 'ListingPageview-offmarket-Nucleus-Paid',
        description: 'User views an edit listing page that requires payment',
    },
    LISTING_PAGE_VIEW_ACTIVE_NON_NUCLEUS: {
        category: 'PostingPath',
        label: 'ListingManagement',
        action: 'ListingPageview-active-Non-Nucleus',
    },
    LISTING_PAGE_VIEW_ACTIVE_NUCLEUS_FREE: {
        category: 'PostingPath',
        label: 'ListingManagement',
        action: 'ListingPageview-active-Nucleus-Free',
    },
    LISTING_PAGE_VIEW_ACTIVE_NUCLEUS_PAID: {
        category: 'PostingPath',
        label: 'ListingManagement',
        action: 'ListingPageview-active-Nucleus-Paid',
        description: 'User views an edit listing page of an active paid listing',
    },
    // BILLING PORTAL
    BILLING_PORTAL_DOWNLOAD_INVOICES: {
        category: 'BillingPortal',
        label: 'Download',
        action: 'DownloadInvoices',
    },
    BILLING_PORTAL_SET_FILTER: {
        category: 'BillingPortal',
        label: WILL_GET_CUSTOM_DATA,
        action: 'SetFilter',
    },
    BILLING_PORTAL_SEARCH: {
        category: 'BillingPortal',
        label: 'Search',
        action: 'SearchByInvoiceNumber',
    },

    // SATELLITE
    PROPERTIES_CONTACTS_ROUTE_CLICK: {
        category: 'LeadManagement',
        label: 'IreneNavigation',
        action: 'ClickFromMyProperties',
    },

    // PROPERTIES PAGE
    BANNER_COVID19_READ_ON_CLICK: {
        category: 'PropertiesPage',
        label: 'Covid19-ReadOn',
        action: 'BannerClick',
    },

    // LISTING CLAIMING
    CLAIM_LISTINGS_CLICK_NEXT: {
        category: CLAIM_LISTING_CATEGORY,
        label: WILL_GET_CUSTOM_DATA,
        action: 'ClicksNext',
    },
    CLAIM_LISTINGS_CLICK_BACK: {
        category: CLAIM_LISTING_CATEGORY,
        label: WILL_GET_CUSTOM_DATA,
        action: 'ClicksBack',
    },
    CLAIM_LISTINGS_CLOSE_LOOKUP_MODAL: {
        cateogry: CLAIM_LISTING_CATEGORY,
        label: WILL_GET_CUSTOM_DATA,
        action: 'CloseLookupModal',
    },
    CLAIM_LISTINGS_CLICK: {
        category: CLAIM_LISTING_CATEGORY,
        label: WILL_GET_CUSTOM_DATA,
        action: WILL_GET_CUSTOM_DATA,
    },
};

const Z_CUSTOM_DIMENSION = {
    ZGUID: customDimensions.ua.zrm.ZUID,
    EVENT_CONTENT: customDimensions.ua.zrm.EVENT_CONTENT,
    ITC: customDimensions.ua.zrm.ITC,
    FULL_REFERRING_URL: customDimensions.ua.zrm.FULL_REFERRING_URL,
    USER_SUMMARY: customDimensions.ua.zrm.RENTAL_MANAGER_USER_SUMMARY,
    AB_TEST_SLOT1: customDimensions.ua.zrm.AB_TEST_NAME, // cd156
    AB_TEST_SLOT2: customDimensions.ua.zrm.AB_TEST_VALUE, // cd157
};

const GTM_CUSTOM_DIMENSION = {
    USER_TOKEN: customDimensions.gtm.USER_TOKEN,
    USER_SUMMARY: customDimensions.gtm.RENTAL_MANAGER_USER_SUMMARY,
    STATE: customDimensions.gtm.PROPERTY_STATE,
    IS_MOBILE_APP: customDimensions.gtm.IS_MOBILE_APP,
};

const AnalyticsConstants = {
    Z_CUSTOM_DIMENSION,
    GA_EVENTS,
    GTM_CUSTOM_DIMENSION,
    LISTING_COMPONENT_GROUP_NAME,
    SUFFIX,
    TOUR_TYPE,
    TOUR_LOCATION,
    CLAIM_LISTINGS_LABEL,
};

export default AnalyticsConstants;
